// src/client/screens/borrower/LoansListScreen.tsx
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Filter } from 'lucide-react';
import { useLoansHistory } from '@client/hooks/useLoan';
import type { LoanFixture } from '@client/lib/fixtures';
import { ngweeToKwachaNumber } from '@client/utils/currency';
import { formatDueIn } from '@client/utils/dates';
import NavBar from '@client/components/NavBar';
import Pill, { type PillTone } from '@client/components/Pill';
import Stat from '@client/components/Stat';

type Filter = 'all' | 'active' | 'repaid';

const STATUS_LABEL: Record<LoanFixture['status'], string> = {
  pending_approval: 'Pending',
  approved: 'Approved',
  disbursement_pending: 'Disbursing',
  active: 'Active',
  repaid: 'Repaid',
  delinquent: 'Overdue',
  default: 'Defaulted',
  restructured: 'Restructured',
  cancelled: 'Cancelled',
  disbursement_failed: 'Failed',
};

const STATUS_TONE: Record<LoanFixture['status'], PillTone> = {
  pending_approval: 'warning',
  approved: 'accent',
  disbursement_pending: 'accent',
  active: 'warning',
  repaid: 'success',
  delinquent: 'danger',
  default: 'danger',
  restructured: 'warning',
  cancelled: 'neutral',
  disbursement_failed: 'danger',
};

function isActive(s: LoanFixture['status']): boolean {
  return s === 'active' || s === 'delinquent' || s === 'restructured'
    || s === 'pending_approval' || s === 'approved' || s === 'disbursement_pending';
}

const ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--c-text)',
  cursor: 'pointer',
};

export default function LoansListScreen() {
  const { data, isLoading, isError } = useLoansHistory();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');

  const totals = useMemo(() => {
    const loans = data ?? [];
    let borrowed = 0;
    let repaid = 0;
    let outstanding = 0;
    for (const l of loans) {
      borrowed += Number(l.principal_ngwee);
      const paid = Number(l.total_due_ngwee) - Number(l.outstanding_ngwee);
      repaid += Math.max(0, paid);
      outstanding += Number(l.outstanding_ngwee);
    }
    return {
      borrowed: borrowed / 100,
      repaid: repaid / 100,
      outstanding: outstanding / 100,
    };
  }, [data]);

  const filtered = useMemo(() => {
    const loans = data ?? [];
    if (filter === 'active') return loans.filter((l) => isActive(l.status));
    if (filter === 'repaid') return loans.filter((l) => l.status === 'repaid');
    return loans;
  }, [data, filter]);

  return (
    <div className="app-shell app-shell--with-nav">
      <main className="scroll-area" style={{ padding: 0 }}>
        {/* Title row */}
        <div style={{
          padding: '20px 24px 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <h1 style={{
            fontSize: 44,
            fontWeight: 700,
            color: 'var(--c-text)',
            letterSpacing: -1.5,
            lineHeight: 1,
            margin: 0,
          }}>
            Loans
          </h1>
          <button type="button" aria-label="Filter loans" style={ICON_BTN}>
            <Filter size={16} />
          </button>
        </div>

        {/* Summary */}
        <div style={{ padding: '16px 24px 0' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            padding: 18,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
          }}>
            <Stat label="Borrowed" value={`K${totals.borrowed.toLocaleString()}`} mono />
            <Stat label="Repaid" value={`K${totals.repaid.toLocaleString()}`} mono />
            <Stat label="Outstanding" value={`K${totals.outstanding.toLocaleString()}`} mono />
          </div>
        </div>

        {/* Filter chips */}
        <div style={{ padding: '20px 24px 8px', display: 'flex', gap: 8 }}>
          {([
            ['all', 'All'],
            ['active', 'Active'],
            ['repaid', 'Repaid'],
          ] as const).map(([id, label]) => {
            const selected = filter === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setFilter(id)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 999,
                  background: selected ? 'var(--c-text)' : 'transparent',
                  color: selected ? 'var(--c-bg)' : 'var(--c-text-sec)',
                  border: `1px solid ${selected ? 'var(--c-text)' : 'var(--c-border)'}`,
                  fontFamily: 'var(--ff-sys)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* States */}
        {isLoading && (
          <p className="small" style={{
            color: 'var(--c-text-muted)', textAlign: 'center', padding: '40px 0',
          }}>
            Loading…
          </p>
        )}

        {isError && (
          <p className="small" role="status" style={{
            color: 'var(--c-danger)', textAlign: 'center', padding: '40px 0',
          }}>
            Couldn&apos;t load your loans. Pull down to retry.
          </p>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <div style={{ padding: '0 24px 24px' }}>
            <div style={{
              background: 'var(--c-surface)',
              border: '1px dashed var(--c-border)',
              borderRadius: 18,
              padding: '40px 24px',
              textAlign: 'center',
            }}>
              <p style={{ margin: 0, color: 'var(--c-text-muted)', fontSize: 15 }}>
                {filter === 'all' ? 'No loans yet' : `No ${filter} loans`}
              </p>
            </div>
          </div>
        )}

        {/* List */}
        {filtered.length > 0 && (
          <div style={{ padding: '12px 24px 0' }}>
            {filtered.map((loan, i) => {
              const tone = STATUS_TONE[loan.status];
              const accentColor =
                tone === 'success' ? 'var(--c-success)' :
                tone === 'danger'  ? 'var(--c-danger)' :
                tone === 'warning' ? 'var(--c-warning)' :
                                     'var(--c-accent)';
              // For non-terminal loans show what is still owed; for closed ones
              // show original principal so the history reads as "borrowed K2,000".
              const isOpen = !['repaid', 'cancelled', 'default'].includes(loan.status);
              const displayAmount = isOpen ? loan.outstanding_ngwee : loan.principal_ngwee;
              const sub =
                loan.status === 'repaid' && loan.repaid_at
                  ? `Closed ${new Date(loan.repaid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                  : loan.due_at
                  ? formatDueIn(loan.due_at)
                  : '';
              return (
                <div
                  key={loan.id}
                  style={{
                    padding: '18px 0',
                    borderBottom: i < filtered.length - 1
                      ? '1px solid var(--c-border-lt)'
                      : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/loans/${loan.id}`)}
                >
                  <div style={{
                    width: 4, height: 44, borderRadius: 2,
                    background: accentColor, opacity: 0.85,
                  }} aria-hidden />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        fontFamily: 'var(--ff-mono)',
                        fontSize: 12,
                        color: 'var(--c-text-muted)',
                        letterSpacing: 0.3,
                      }}>
                        {loan.loan_number}
                      </div>
                      <Pill tone={tone}>{STATUS_LABEL[loan.status]}</Pill>
                    </div>
                    <div style={{
                      fontSize: 24,
                      fontWeight: 500,
                      color: 'var(--c-text)',
                      letterSpacing: -0.5,
                      marginTop: 2,
                      lineHeight: 1.1,
                    }}>
                      K{ngweeToKwachaNumber(displayAmount).toLocaleString()}.00
                    </div>
                    {sub && (
                      <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 4 }}>
                        {sub}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={18} color="var(--c-text-dim)" />
                </div>
              );
            })}
          </div>
        )}

        <div style={{ height: 24 }} />
      </main>

      <NavBar />
    </div>
  );
}
